<template>
  <div>
  <b-modal
    id="modal-invoice-view"
    ref="modal"
    size="lg"
    hide-footer
    modal-class="invoice-view-modal-shell"
    content-class="invoice-view-modal-content"
    body-class="invoice-view-modal-body"
    @hide="onHide"
    @hidden="onHidden"
    @shown="onShown"
  >
    <template #modal-title>
      <div class="invoice-view-modal-title">
        <span>Invoice Details</span>
        <b-badge
          v-if="activeInvoice"
          :variant="statusVariant"
          class="ml-75 invoice-view-status"
        >
          {{ statusWithRevision }}
        </b-badge>
        <span v-if="revisionTimesLabel" class="invoice-view-revised-times ml-50">
          {{ revisionTimesLabel }}
        </span>
        <b-button
          v-if="showHistoryAction"
          size="sm"
          variant="link"
          class="invoice-view-history-link ml-25 p-0"
          @click="openRevisionHistory"
        >
          View history
        </b-button>
        <span
          v-if="activeInvoice && (activeInvoice.has_signed_invoice || activeInvoice.signed_invoice || activeInvoice.signed_invoice_path)"
          class="invoice-view-signed ml-50"
        >
          <feather-icon icon="CheckCircleIcon" size="12" class="mr-25" />
          Signed
        </span>
      </div>
    </template>

    <div v-if="activeInvoice" class="invoice-view-modal">
      <div class="invoice-view-hero">
        <div>
          <div class="invoice-view-hero__id">
            {{ activeInvoice.invoice_id || activeInvoice.invoice_number || 'N/A' }}
          </div>
          <div class="invoice-view-hero__meta text-muted">
            {{ formatDate(activeInvoice.invoice_date) }}
            <span v-if="activeInvoice.client_name || activeInvoice.client"> · {{ activeInvoice.client_name || activeInvoice.client }}</span>
          </div>
        </div>
        <div class="invoice-view-hero__amount">
          <div class="invoice-view-hero__amount-label">Receivable</div>
          <div class="invoice-view-hero__amount-value">₹{{ formatAmount(activeInvoice.total_amount) }}</div>
        </div>
      </div>

      <b-row class="invoice-view-grid">
        <b-col cols="12" md="6">
          <dl class="invoice-view-dl">
            <div class="invoice-view-dl__row">
              <dt>Client</dt>
              <dd>
                <div>{{ activeInvoice.client_name || activeInvoice.client || 'N/A' }}</div>
                <small v-if="activeInvoice.client_gst" class="inv-secondary-text">{{ activeInvoice.client_gst }}</small>
              </dd>
            </div>
            <div class="invoice-view-dl__row">
              <dt>Candidate</dt>
              <dd>{{ activeInvoice.candidate_display || activeInvoice.candidate_name || '—' }}</dd>
            </div>
            <div class="invoice-view-dl__row">
              <dt>Category</dt>
              <dd>{{ activeInvoice.category || '—' }}</dd>
            </div>
            <div class="invoice-view-dl__row">
              <dt>Billing Model</dt>
              <dd>{{ billingShortLabel }}</dd>
            </div>
          </dl>
        </b-col>
        <b-col cols="12" md="6">
          <dl class="invoice-view-dl">
            <div class="invoice-view-dl__row">
              <dt>Base Amount</dt>
              <dd>₹{{ formatAmount(activeInvoice.base_amount) }}</dd>
            </div>
            <div class="invoice-view-dl__row">
              <dt>Paid</dt>
              <dd>₹{{ formatAmount(activeInvoice.paid_amount) }}</dd>
            </div>
            <div class="invoice-view-dl__row">
              <dt>Outstanding</dt>
              <dd :class="(activeInvoice.outstanding_amount || 0) > 0 ? 'text-danger font-weight-bold' : 'text-success'">
                ₹{{ formatAmount(activeInvoice.outstanding_amount) }}
              </dd>
            </div>
            <div class="invoice-view-dl__row">
              <dt>Due Date</dt>
              <dd :class="isOverdue ? 'text-danger font-weight-bold' : ''">
                {{ formatDate(activeInvoice.due_date) }}
              </dd>
            </div>
            <div class="invoice-view-dl__row">
              <dt>PO Number</dt>
              <dd>{{ activeInvoice.po_number || '—' }}</dd>
            </div>
          </dl>
        </b-col>
      </b-row>

      <div v-if="activeInvoice.description || activeInvoice.comment" class="invoice-view-notes">
        <div v-if="activeInvoice.description" class="invoice-view-note">
          <div class="invoice-view-note__label">Description</div>
          <div class="invoice-view-note__body">{{ activeInvoice.description }}</div>
        </div>
        <div v-if="activeInvoice.comment" class="invoice-view-note">
          <div class="invoice-view-note__label">Comment</div>
          <div class="invoice-view-note__body">{{ activeInvoice.comment }}</div>
        </div>
      </div>

      <div class="invoice-view-supporting">
        <span class="invoice-view-supporting__label">Supporting documents</span>
        <template v-if="supportingDocuments.length">
          <span
            v-for="doc in supportingDocuments"
            :key="doc.key"
            class="invoice-view-supporting__chip"
            :class="{ 'invoice-view-supporting__chip--openable': canOpenSupportingDoc(doc) }"
          >
            <button
              v-if="canOpenSupportingDoc(doc)"
              type="button"
              class="invoice-view-supporting__open"
              :title="`View ${doc.name}`"
              @click="viewSupportingDoc(doc)"
            >
              <feather-icon icon="PaperclipIcon" size="11" class="flex-shrink-0" />
              <span class="invoice-view-supporting__name invoice-view-supporting__name--link">{{ doc.name }}</span>
            </button>
            <template v-else>
              <feather-icon icon="PaperclipIcon" size="11" class="flex-shrink-0" />
              <span class="invoice-view-supporting__name" :title="doc.name">{{ doc.name }}</span>
            </template>
            <button
              v-if="canOpenSupportingDoc(doc) && canDownloadInvoices"
              type="button"
              class="invoice-view-supporting__dl"
              aria-label="Download supporting document"
              title="Download"
              @click.stop="downloadSupportingDoc(doc)"
            >
              <feather-icon icon="DownloadIcon" size="11" />
            </button>
          </span>
        </template>
        <span v-else class="invoice-view-supporting__empty">No supporting documents</span>
      </div>

      <div class="invoice-view-signed-section">
        <div class="invoice-view-section-label">Signed Invoice</div>
        <signed-invoice-workflow
          ref="signedWorkflow"
          :invoice="activeInvoice"
          :invoice-id="resolvedInvoiceId"
          :can-download="canDownloadInvoices"
          @signed-uploaded="onSignedUploaded"
          @generated-pdf-stored="onGeneratedStored"
        />
      </div>

      <div class="invoice-view-versions-section mt-2">
        <invoice-versions-section
          :invoice-id="resolvedInvoiceId"
          :invoice="activeInvoice"
          :invoice-status="activeInvoice.status"
          :existing-file-path="activeInvoice.file || activeInvoice.file_path || null"
          :revision-history="revisionHistory"
          :revision-history-loaded="revisionHistoryLoaded"
          :revision-history-source="revisionHistorySource"
          history-modal-id="modal-invoice-revision-history-detail"
        />
      </div>

      <div class="invoice-view-timeline-section mt-2">
        <invoice-timeline-card
          :invoice-timeline="invoiceTimeline"
          :loading="invoiceTimelineLoading"
          :invoice-status="(activeInvoice && activeInvoice.status) || ''"
          :timeline-meta="invoiceTimelineMeta"
        />
      </div>

      <div class="invoice-view-footer">
        <b-button variant="outline-secondary" size="sm" class="invoice-view-footer__btn" @click="closeModal">Close</b-button>
        <b-button
          v-if="$can('create','invoice')"
          variant="outline-primary"
          size="sm"
          class="invoice-view-footer__btn"
          @click="$emit('duplicate', activeInvoice)"
        >
          <feather-icon icon="CopyIcon" size="14" class="mr-25" />
          Duplicate for next month
        </b-button>
        <b-button
          v-if="$can('update','invoice')"
          variant="primary"
          size="sm"
          class="invoice-view-footer__btn"
          @click="$emit('edit', activeInvoice)"
        >
          <feather-icon icon="EditIcon" size="14" class="mr-25" />
          Edit Invoice
        </b-button>
      </div>
    </div>
  </b-modal>

  <invoice-revision-history-modal
    modal-id="modal-invoice-revision-history-detail"
    :invoice="activeInvoice"
    :revision-history="revisionHistoryLoaded ? revisionHistory : null"
  />

  <b-modal
    id="modal-invoice-supporting-preview"
    ref="docPreviewModal"
    :title="docPreviewTitle"
    size="xl"
    centered
    no-enforce-focus
    modal-class="invoice-doc-preview-shell"
    body-class="invoice-doc-preview-body"
    @hidden="resetDocPreview"
  >
    <div v-if="docPreviewLoading" class="text-center text-muted py-5">
      Loading document…
    </div>
    <div v-else-if="docPreviewKind === 'pdf' && docPreviewSrc">
      <iframe
        :src="docPreviewSrc"
        frameborder="0"
        width="100%"
        title="Supporting document preview"
        class="invoice-doc-preview-frame"
      />
    </div>
    <div v-else-if="docPreviewKind === 'image' && docPreviewSrc" class="text-center">
      <img
        :src="docPreviewSrc"
        alt="Supporting document preview"
        class="invoice-doc-preview-image"
      />
    </div>
    <div v-else-if="docPreviewKind === 'text' && docPreviewSrc">
      <iframe
        :src="docPreviewSrc"
        frameborder="0"
        width="100%"
        title="Supporting document preview"
        class="invoice-doc-preview-frame invoice-doc-preview-frame--text"
      />
    </div>
    <div v-else class="text-center text-muted py-3">
      Preview is not available for this file type. Use Download to save it.
    </div>
    <template #modal-footer>
      <b-button
        v-if="canDownloadInvoices"
        variant="outline-secondary"
        :disabled="docPreviewLoading || !docPreviewDoc"
        @click="downloadSupportingDoc(docPreviewDoc)"
      >
        <feather-icon icon="DownloadIcon" size="14" class="mr-50" />
        Download
      </b-button>
      <b-button variant="primary" @click="closeDocPreview">Close</b-button>
    </template>
  </b-modal>
  </div>
</template>

<script>
import { BModal, BButton, BRow, BCol, BBadge } from 'bootstrap-vue'
import moment from 'moment'
import SignedInvoiceWorkflow from './SignedInvoiceWorkflow.vue'
import InvoiceTimelineCard from './InvoiceTimelineCard.vue'
import InvoiceVersionsSection from './InvoiceVersionsSection.vue'
import InvoiceRevisionHistoryModal from './modals/InvoiceRevisionHistoryModal.vue'
import {
  formatRevisionTimesLabel,
  formatStatusWithRevisionCount,
  getInvoiceRevisionCount,
  isRevisedStatus,
} from '@/utils/invoice-revision-count'
import invoiceTimelineService from '@/services/invoice-timeline-service'
import enhancedInvoiceRevisionService from '@/services/enhanced-invoice-revision-service'
import invoiceService from '@/services/invoice-service'
import { resolveInvoiceApiKey } from '@/utils/invoice-api-key'
import { canDownloadInvoices as checkCanDownloadInvoices } from '@/utils/invoice-permissions'
import {
  extractSupportingDocuments,
  supportingDocCanOpen,
  resolveInvoiceAttachmentPreview,
  downloadInvoiceAttachment,
} from '@/utils/invoice-file-loader'

export default {
  name: 'InvoiceViewModal',
  components: {
    BModal,
    BButton,
    BRow,
    BCol,
    BBadge,
    SignedInvoiceWorkflow,
    InvoiceTimelineCard,
    InvoiceVersionsSection,
    InvoiceRevisionHistoryModal,
  },
  props: {
    invoice: { type: Object, default: null }
  },
  data() {
    return {
      // Local copy so parent clearing `invoice` does not blank an open modal body.
      activeInvoice: null,
      closingKey: null,
      invoiceTimeline: [],
      invoiceTimelineLoading: false,
      invoiceTimelineMeta: null,
      revisionHistory: [],
      revisionHistoryLoaded: false,
      revisionHistorySource: null,
      docPreviewLoading: false,
      docPreviewKind: null,
      docPreviewSrc: null,
      docPreviewRevoke: false,
      docPreviewTitle: 'Supporting document',
      docPreviewDoc: null,
    }
  },
  computed: {
    canDownloadInvoices() {
      return checkCanDownloadInvoices(this.$can.bind(this))
    },
    resolvedInvoiceId() {
      return resolveInvoiceApiKey(this.activeInvoice)
    },
    billingShortLabel() {
      const m = ((this.activeInvoice && this.activeInvoice.billing_model) || '').toLowerCase()
      if (m.includes('permanent') || m.includes('fte') || m.includes('recruitment')) return 'FTE'
      if (m.includes('contractual') || m.includes('staffing')) return 'Contractual'
      if (m.includes('cost')) return 'Cost+'
      if (m.includes('payroll')) return 'Payroll'
      return (this.activeInvoice && this.activeInvoice.billing_model) || 'N/A'
    },
    statusVariant() {
      const v = {
        Draft: 'secondary',
        'Pending Approval': 'warning',
        Approved: 'info',
        Sent: 'primary',
        Raised: 'primary',
        Issued: 'info',
        'Partially Paid': 'warning',
        'Part Paid': 'warning',
        Paid: 'success',
        Overdue: 'danger',
        Cancelled: 'dark',
        Revised: 'info',
      }
      return v[(this.activeInvoice && this.activeInvoice.status)] || 'secondary'
    },
    revisionCount() {
      return getInvoiceRevisionCount(
        this.activeInvoice,
        this.revisionHistoryLoaded ? this.revisionHistory : [],
      )
    },
    statusWithRevision() {
      if (!this.activeInvoice) return '—'
      return formatStatusWithRevisionCount(this.activeInvoice.status, this.revisionCount)
    },
    revisionTimesLabel() {
      return formatRevisionTimesLabel(this.revisionCount)
    },
    showHistoryAction() {
      return isRevisedStatus(this.activeInvoice && this.activeInvoice.status) || this.revisionCount > 0
    },
    isOverdue() {
      if (!this.activeInvoice || !this.activeInvoice.due_date) return false
      return moment(this.activeInvoice.due_date).isBefore(moment(), 'day')
    },
    supportingDocuments() {
      return extractSupportingDocuments(this.activeInvoice)
    },
  },
  watch: {
    invoice: {
      immediate: true,
      handler(val) {
        if (val) this.activeInvoice = val
      }
    }
  },
  beforeDestroy() {
    this.revokeDocPreviewSrc()
  },
  methods: {
    formatAmount(num) {
      if (num === null || num === undefined || isNaN(num)) return '0'
      return parseFloat(num).toLocaleString('en-IN', { maximumFractionDigits: 2 })
    },
    formatDate(d) {
      if (!d) return 'N/A'
      try {
        return moment(d).format('DD-MMM-YYYY')
      } catch (_) {
        return 'N/A'
      }
    },
    closeModal() {
      // Hide first — do NOT emit close here. Emitting close clears the parent
      // prop while the modal is still open, which blanks the body (v-if).
      if (this.$refs.modal) this.$refs.modal.hide()
      else this.$bvModal.hide('modal-invoice-view')
    },
    onHide() {
      this.closingKey = resolveInvoiceApiKey(this.activeInvoice)
    },
    async onShown() {
      if (this.$refs.signedWorkflow) this.$refs.signedWorkflow.refresh()
      await Promise.all([
        this.enrichSupportingDocuments(),
        this.loadRevisionHistory().then(() => this.loadInvoiceTimeline()),
      ])
    },
    canOpenSupportingDoc(doc) {
      return supportingDocCanOpen(doc)
    },
    revokeDocPreviewSrc() {
      if (this.docPreviewRevoke && this.docPreviewSrc && String(this.docPreviewSrc).startsWith('blob:')) {
        try { URL.revokeObjectURL(this.docPreviewSrc) } catch (_) { /* already revoked */ }
      }
      this.docPreviewRevoke = false
      this.docPreviewSrc = null
    },
    resetDocPreview() {
      this.revokeDocPreviewSrc()
      this.docPreviewLoading = false
      this.docPreviewKind = null
      this.docPreviewTitle = 'Supporting document'
      this.docPreviewDoc = null
    },
    closeDocPreview() {
      if (this.$refs.docPreviewModal) this.$refs.docPreviewModal.hide()
      else this.$bvModal.hide('modal-invoice-supporting-preview')
    },
    async viewSupportingDoc(doc) {
      if (!this.canOpenSupportingDoc(doc)) return
      this.revokeDocPreviewSrc()
      this.docPreviewDoc = doc
      this.docPreviewTitle = doc.name || 'Supporting document'
      this.docPreviewKind = null
      this.docPreviewLoading = true
      if (this.$refs.docPreviewModal) this.$refs.docPreviewModal.show()
      else this.$bvModal.show('modal-invoice-supporting-preview')

      const preview = await resolveInvoiceAttachmentPreview(doc)
      if (!preview || !preview.src) {
        this.docPreviewLoading = false
        this.closeDocPreview()
        this.$bvToast.toast('Could not open this supporting document.', {
          title: 'Supporting document',
          variant: 'warning',
          solid: true,
        })
        return
      }
      this.docPreviewKind = preview.kind
      this.docPreviewSrc = preview.src
      this.docPreviewRevoke = !!preview.revoke
      this.docPreviewLoading = false
    },
    async downloadSupportingDoc(doc) {
      if (!this.canDownloadInvoices) return
      const ok = await downloadInvoiceAttachment(doc)
      if (!ok) {
        this.$bvToast.toast('Could not download this supporting document.', {
          title: 'Supporting document',
          variant: 'warning',
          solid: true,
        })
      }
    },
    async enrichSupportingDocuments() {
      const id = this.resolvedInvoiceId
      if (!id || !this.activeInvoice) return
      try {
        const res = await invoiceService.getById(id)
        if (!res || (res.status !== 'OK' && res.status !== 200)) return
        const raw = res.data
        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return
        const nested = (raw.invoice && typeof raw.invoice === 'object' && !Array.isArray(raw.invoice))
          ? raw.invoice
          : null
        const detail = nested ? { ...raw, ...nested } : raw
        const keys = [
          'supporting_document',
          'supporting_documents',
          'supportingDocuments',
          'supporting_docs',
          'supportingDocument',
          'invoiceFile',
          'invoice_file',
          'invoice_file_url',
          'invoice_file_path',
          'files',
          'document_urls',
          'documentUrls',
          'file',
          'file_path',
          'revision_count',
          'revise_count',
          'metadata',
        ]
        const patch = {}
        keys.forEach((key) => {
          if (detail[key] != null && detail[key] !== '') patch[key] = detail[key]
        })
        if (Object.keys(patch).length) {
          this.activeInvoice = { ...this.activeInvoice, ...patch }
        }
      } catch (_) {
        // List payload fields are enough when GET /invoice/:id is unavailable.
      }
    },
    onHidden() {
      const closedKey = this.closingKey
      this.closingKey = null
      const nextKey = resolveInvoiceApiKey(this.invoice)

      // Stale hide finished after a newer invoice was selected — keep content and re-show.
      if (nextKey && closedKey && String(nextKey) !== String(closedKey)) {
        this.activeInvoice = this.invoice
        this.$nextTick(() => {
          if (this.$refs.modal) this.$refs.modal.show()
          else this.$bvModal.show('modal-invoice-view')
        })
        return
      }

      this.activeInvoice = null
      this.closeDocPreview()
      this.resetDocPreview()
      this.$emit('close')
    },
    openRevisionHistory() {
      if (this.$bvModal) this.$bvModal.show('modal-invoice-revision-history-detail')
    },
    onSignedUploaded(payload) {
      this.loadInvoiceTimeline()
      this.$emit('signed-uploaded', { invoice: this.activeInvoice, payload })
    },
    onGeneratedStored(payload) {
      this.loadInvoiceTimeline()
      this.$emit('generated-pdf-stored', { invoice: this.activeInvoice, payload })
    },
    async loadRevisionHistory() {
      const id = this.resolvedInvoiceId
      if (!id) {
        this.revisionHistory = []
        this.revisionHistoryLoaded = true
        this.revisionHistorySource = 'empty'
        return
      }
      this.revisionHistoryLoaded = false
      try {
        const res = await enhancedInvoiceRevisionService.getRevisionHistory(id)
        const { rows, source } = enhancedInvoiceRevisionService.extractRevisionRows(res)
        this.revisionHistory = rows
        this.revisionHistorySource = source
      } catch (_) {
        this.revisionHistory = []
        this.revisionHistorySource = 'error'
      } finally {
        this.revisionHistoryLoaded = true
      }
    },
    async loadInvoiceTimeline() {
      const id = this.resolvedInvoiceId
      if (!id) {
        this.invoiceTimeline = []
        this.invoiceTimelineMeta = null
        return
      }
      this.invoiceTimelineLoading = true
      try {
        const res = await invoiceTimelineService.getInvoiceTimeline(id, this.activeInvoice, {
          revisions: this.revisionHistoryLoaded ? this.revisionHistory : undefined,
          revisionSource: this.revisionHistorySource || undefined,
        })
        this.invoiceTimeline = (res && Array.isArray(res.data)) ? res.data : []
        this.invoiceTimelineMeta = (res && res._meta) || null
      } catch (_) {
        this.invoiceTimeline = []
        this.invoiceTimelineMeta = null
      } finally {
        this.invoiceTimelineLoading = false
      }
    },
  }
}
</script>

<style lang="scss">
$inv-teal: #0099AC;
$inv-border: #e8ecf0;
$inv-muted: #64748b;
$inv-bg: #f7f9fb;

.invoice-view-modal-shell {
  .modal-header {
    padding: 0.85rem 1.1rem;
    border-bottom: 1px solid $inv-border;
  }

  .modal-title {
    width: 100%;
  }

  .modal-dialog {
    max-width: 800px;
  }
}

.invoice-view-modal-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  font-size: 1rem;
  font-weight: 650;
  color: #1e293b;
}

.invoice-view-status {
  font-size: 0.68rem;
  font-weight: 650;
}

.invoice-view-signed {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 650;
  color: #2f9e6f;
}

.invoice-view-revised-times {
  font-size: 0.72rem;
  font-weight: 650;
  color: #475569;
}

.invoice-view-history-link {
  font-size: 0.72rem;
  font-weight: 650;
}

.invoice-view-modal-body {
  padding: 1rem 1.1rem 1.1rem !important;
}

.invoice-view-hero {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 0.95rem;
  margin-bottom: 0.85rem;
  background: $inv-bg;
  border: 1px solid $inv-border;
  border-radius: 10px;
}

.invoice-view-hero__id {
  font-size: 1.05rem;
  font-weight: 700;
  color: $inv-teal;
  line-height: 1.2;
}

.invoice-view-hero__meta {
  margin-top: 0.2rem;
  font-size: 0.78rem;
}

.inv-secondary-text {
  color: #475569;
  font-size: 0.75rem;
  line-height: 1.25;
}

.invoice-view-hero__amount {
  text-align: right;
}

.invoice-view-hero__amount-label {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $inv-muted;
}

.invoice-view-hero__amount-value {
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}

.invoice-view-dl {
  margin: 0;

  &__row {
    display: grid;
    grid-template-columns: 110px 1fr;
    gap: 0.35rem 0.75rem;
    padding: 0.35rem 0;
    border-bottom: 1px solid #f1f5f9;

    dt {
      margin: 0;
      font-size: 0.72rem;
      font-weight: 650;
      color: $inv-muted;
    }

    dd {
      margin: 0;
      font-size: 0.84rem;
      color: #1e293b;
    }
  }
}

.invoice-view-notes {
  margin-top: 0.75rem;
}

.invoice-view-note {
  margin-bottom: 0.55rem;

  &__label {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $inv-muted;
    margin-bottom: 0.25rem;
  }

  &__body {
    padding: 0.55rem 0.7rem;
    background: $inv-bg;
    border: 1px solid $inv-border;
    border-radius: 8px;
    font-size: 0.84rem;
    color: #334155;
  }
}

.invoice-view-supporting {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.3rem 0.45rem;
  margin-top: 0.55rem;
  min-height: 1.35rem;
}

.invoice-view-supporting__label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $inv-muted;
  flex-shrink: 0;
}

.invoice-view-supporting__empty {
  font-size: 0.78rem;
  color: $inv-muted;
}

.invoice-view-supporting__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  max-width: 220px;
  padding: 0.1rem 0.4rem;
  border: 1px solid $inv-border;
  border-radius: 999px;
  background: $inv-bg;
  color: $inv-teal;
  line-height: 1.25;
}

.invoice-view-supporting__chip--openable {
  cursor: pointer;
}

.invoice-view-supporting__open {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.invoice-view-supporting__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.75rem;
  color: #1e293b;
  border: 0;
  background: transparent;
  padding: 0;
  max-width: 150px;
}

.invoice-view-supporting__name--link {
  color: $inv-teal;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.invoice-view-supporting__dl {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: $inv-teal;
  line-height: 1;
  cursor: pointer;
}

.invoice-doc-preview-shell {
  z-index: 1065;

  .modal-dialog {
    max-width: 960px;
  }
}

.invoice-doc-preview-body {
  padding: 0.75rem 1rem !important;
}

.invoice-doc-preview-frame {
  min-height: 70vh;
  height: 70vh;
  border: 0;
  background: #f8f8f8;
}

.invoice-doc-preview-frame--text {
  min-height: 40vh;
  height: 40vh;
}

.invoice-doc-preview-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

.invoice-view-signed-section {
  margin-top: 1rem;
  padding-top: 0.85rem;
  border-top: 1px solid $inv-border;
}

.invoice-view-section-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $inv-muted;
  margin-bottom: 0.55rem;
}

.invoice-view-footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 0.85rem;
  border-top: 1px solid $inv-border;

  &__btn {
    min-height: 2.5rem;
  }
}

@media (max-width: 991.98px) {
  .invoice-view-modal-shell {
    .modal-dialog {
      max-width: calc(100% - 1.5rem);
      margin: 0.75rem auto;
    }
  }
}

@media (max-width: 767.98px) {
  .invoice-view-modal-body {
    padding: 0.85rem !important;
  }

  .invoice-view-hero {
    padding: 0.75rem;
  }
}

@media (max-width: 575.98px) {
  .invoice-view-modal-shell {
    .modal-dialog {
      max-width: 100%;
      margin: 0;
      min-height: 100%;
    }

    .modal-content {
      border-radius: 0;
      min-height: 100vh;
    }

    .modal-header {
      padding: 0.75rem 0.85rem;
    }
  }

  .invoice-view-modal-body {
    padding: 0.75rem !important;
  }

  .invoice-view-dl__row {
    grid-template-columns: 1fr;
    gap: 0.1rem;
  }

  .invoice-view-hero__amount {
    text-align: left;
  }

  .invoice-view-footer {
    flex-direction: column;
    align-items: stretch;

    &__btn {
      width: 100%;
      margin: 0 !important;
    }
  }
}
</style>
